import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../Live/Loader";
import {
  FiBook,
  FiDollarSign,
  FiAward,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiTrendingUp,
  FiChevronDown,
  FiChevronUp,
  FiPercent,
  FiTarget,
  FiActivity,
} from "react-icons/fi";

/** API may return decimals as strings; always coerce before .toFixed(). */
function formatDecimal(value, fractionDigits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return (0).toFixed(fractionDigits);
  return n.toFixed(fractionDigits);
}

/** exam_type from API: course_exam | placement_exam */
function getExamTypeLabel(exam) {
  if (exam?.exam_type === "placement_exam") return "Placement test";
  return "Regular exam";
}

function ExamTypeBadge({ exam }) {
  const isPlacement = exam?.exam_type === "placement_exam";
  return (
    <span
      className={`inline-flex items-center mt-0.5 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
        isPlacement
          ? "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
      }`}
    >
      {getExamTypeLabel(exam)}
    </span>
  );
}

const StudentHistory = ({ studentData, loading: externalLoading }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedCourse, setExpandedCourse] = useState(null);

  // Extract history data from studentData (comes from backend API)
  const historyData = useMemo(() => {
    if (!studentData?.history) {
      return {
        courses: [],
        payments: [],
        examAttempts: [],
        scores: [],
        courseResults: [],
        statistics: null,
      };
    }

    return {
      courses: studentData.history.courses || [],
      payments: studentData.history.payments || [],
      examAttempts: studentData.history.exam_attempts || [],
      scores: studentData.history.scores || [],
      courseResults: studentData.history.course_results || [],
      statistics: studentData.history.statistics || null,
    };
  }, [studentData]);

  const tabs = [
    { id: "overview", label: "Overview", icon: FiActivity },
    { id: "courses", label: "Courses", icon: FiBook, count: historyData.courses.length },
    { id: "payments", label: "Payments", icon: FiDollarSign, count: historyData.payments.length },
    { id: "exams", label: "Exams", icon: FiFileText, count: historyData.examAttempts.length },
    { id: "scores", label: "Scores", icon: FiAward, count: historyData.scores.length },
  ];

  // Use statistics from backend or calculate from local data
  const stats = useMemo(() => {
    if (historyData.statistics) {
      return {
        totalCourses: historyData.statistics.courses?.total || 0,
        activeCourses: historyData.statistics.courses?.active || 0,
        pendingCourses: historyData.statistics.courses?.pending || 0,
        totalPayments: historyData.statistics.payments?.total || 0,
        verifiedPayments: historyData.statistics.payments?.verified || 0,
        pendingPayments: historyData.statistics.payments?.pending || 0,
        totalExams: historyData.statistics.exams?.total || 0,
        completedExams: historyData.statistics.exams?.completed || 0,
        passedExams: historyData.statistics.scores?.passed || 0,
        averageScore: historyData.statistics.scores?.average_percentage || 0,
      };
    }

    // Fallback to local calculation if statistics not provided
    return {
      totalCourses: historyData.courses.length,
      activeCourses: historyData.courses.filter(c => c.payment_status === 'verified').length,
      pendingCourses: historyData.courses.filter(c => c.payment_status === 'pending').length,
      totalPayments: historyData.payments.length,
      verifiedPayments: historyData.payments.filter(p => p.status === 'verified').length,
      pendingPayments: historyData.payments.filter(p => p.status === 'pending').length,
      totalExams: historyData.examAttempts.length,
      completedExams: historyData.examAttempts.filter(a => a.status === 'completed').length,
      passedExams: historyData.scores.filter(s => s.is_passed).length,
      averageScore: historyData.scores.length > 0
        ? formatDecimal(
            historyData.scores.reduce((sum, s) => sum + Number(s.percentage || 0), 0) /
              historyData.scores.length
          )
        : "0.0",
    };
  }, [historyData]);

  if (externalLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  // Check if history data exists
  if (!studentData?.history) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
        <FiActivity className="w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No history data available</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
            <FiActivity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Student History
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Complete academic and payment records
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id
                  ? "bg-white/20 text-white"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === "overview" && (
            <OverviewTab stats={stats} historyData={historyData} />
          )}
          {activeTab === "courses" && (
            <CoursesTab 
              courses={historyData.courses} 
              expandedCourse={expandedCourse}
              setExpandedCourse={setExpandedCourse}
            />
          )}
          {activeTab === "payments" && (
            <PaymentsTab payments={historyData.payments} />
          )}
          {activeTab === "exams" && (
            <ExamsTab examAttempts={historyData.examAttempts} />
          )}
          {activeTab === "scores" && (
            <ScoresTab scores={historyData.scores} courseResults={historyData.courseResults} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats, historyData }) => (
  <div className="space-y-6">
    {/* Quick Stats Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard
        icon={FiBook}
        label="Enrolled Courses"
        value={stats.totalCourses}
        subValue={`${stats.activeCourses} active`}
        color="blue"
      />
      <StatCard
        icon={FiDollarSign}
        label="Total Payments"
        value={stats.totalPayments}
        subValue={`${stats.verifiedPayments} verified`}
        color="green"
      />
      <StatCard
        icon={FiFileText}
        label="Exams Taken"
        value={stats.totalExams}
        subValue={`${stats.completedExams} completed`}
        color="purple"
      />
      <StatCard
        icon={FiPercent}
        label="Average Score"
        value={`${formatDecimal(stats.averageScore)}%`}
        subValue={`${stats.passedExams} passed`}
        color="orange"
      />
    </div>

    {/* Recent Activity */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Courses */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiBook className="w-4 h-4 text-blue-500" />
          Recent Courses
        </h3>
        <div className="space-y-3">
          {historyData.courses.slice(0, 3).map((course, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{course.title}</p>
                <p className="text-xs text-gray-500">{course.instructor_name}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                course.payment_status === 'verified'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}>
                {course.payment_status}
              </span>
            </div>
          ))}
          {historyData.courses.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No courses enrolled</p>
          )}
        </div>
      </div>

      {/* Recent Scores */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiAward className="w-4 h-4 text-orange-500" />
          Recent Exam Scores
        </h3>
        <div className="space-y-3">
          {historyData.scores.slice(0, 3).map((score, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
              <div className="min-w-0 pr-2">
                <p className="font-medium text-gray-900 dark:text-white truncate">{score.exam?.title || 'Exam'}</p>
                <ExamTypeBadge exam={score.exam} />
                <p className="text-xs text-gray-500 mt-1">{score.course?.title || 'Course'}</p>
              </div>
              <div className="text-right">
                <p className={`font-bold ${score.is_passed ? 'text-green-600' : 'text-red-600'}`}>
                  {formatDecimal(score.percentage)}%
                </p>
                <p className="text-xs text-gray-500">{score.is_passed ? 'Passed' : 'Failed'}</p>
              </div>
            </div>
          ))}
          {historyData.scores.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No exam scores yet</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    green: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    orange: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subValue}</p>
    </div>
  );
};

// Courses Tab Component
const CoursesTab = ({ courses, expandedCourse, setExpandedCourse }) => (
  <div className="space-y-4">
    {courses.length === 0 ? (
      <EmptyState icon={FiBook} message="No courses enrolled yet" />
    ) : (
      courses.map((course, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
            onClick={() => setExpandedCourse(expandedCourse === index ? null : index)}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                course.payment_status === 'verified'
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-yellow-100 dark:bg-yellow-900/30'
              }`}>
                <FiBook className={`w-6 h-6 ${
                  course.payment_status === 'verified'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">{course.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {course.instructor_name} • {course.duration || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                course.payment_status === 'verified'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}>
                {course.payment_status}
              </span>
              {expandedCourse === index ? <FiChevronUp /> : <FiChevronDown />}
            </div>
          </div>
          
          <AnimatePresence>
            {expandedCourse === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200 dark:border-gray-700"
              >
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 dark:bg-gray-700/30">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Level</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.level || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Price</p>
                    <p className="font-medium text-gray-900 dark:text-white">${course.price || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Category</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.category?.title || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">{course.status || 'N/A'}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))
    )}
  </div>
);

// Payments Tab Component
const PaymentsTab = ({ payments }) => (
  <div className="space-y-4">
    {payments.length === 0 ? (
      <EmptyState icon={FiDollarSign} message="No payments recorded" />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Course</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {payments.map((payment, index) => (
              <tr key={index} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                  {payment.course?.title || payment.course_title || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 capitalize">
                  {payment.method || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                  ${payment.course?.price || 0}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                    payment.status === 'verified'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {payment.status === 'verified' ? <FiCheckCircle className="w-3 h-3" /> : <FiClock className="w-3 h-3" />}
                    {payment.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {payment.created_at}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Exams Tab Component
const ExamsTab = ({ examAttempts }) => (
  <div className="space-y-4">
    {examAttempts.length === 0 ? (
      <EmptyState icon={FiFileText} message="No exams taken yet" />
    ) : (
      examAttempts.map((attempt, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                attempt.status === 'completed' 
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : attempt.status === 'in_progress'
                  ? 'bg-blue-100 dark:bg-blue-900/30'
                  : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                {attempt.status === 'completed' ? (
                  <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                ) : attempt.status === 'in_progress' ? (
                  <FiClock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                ) : (
                  <FiXCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                )}
              </div>
              <div className="min-w-0">
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {attempt.exam?.title || 'Exam'}
                </h4>
                <ExamTypeBadge exam={attempt.exam} />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {attempt.exam?.course?.title || 'Course'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                attempt.status === 'completed'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : attempt.status === 'in_progress'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {attempt.status?.replace('_', ' ')}
              </span>
              <p className="text-xs text-gray-500 mt-1">{attempt.created_at}</p>
            </div>
          </div>
          {attempt.is_violation && (
            <div className="mt-3 flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
              <FiAlertTriangle className="w-4 h-4" />
              <span>Violation recorded ({attempt.violation_count || 0} times)</span>
            </div>
          )}
        </motion.div>
      ))
    )}
  </div>
);

// Scores Tab Component
const ScoresTab = ({ scores, courseResults }) => (
  <div className="space-y-6">
    {/* Individual Exam Scores */}
    <div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <FiTarget className="w-4 h-4 text-purple-500" />
        Exam Scores
      </h3>
      {scores.length === 0 ? (
        <EmptyState icon={FiAward} message="No exam scores yet" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scores.map((score, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-xl border-2 ${
                score.is_passed
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`text-2xl font-bold ${score.is_passed ? 'text-green-600' : 'text-red-600'}`}>
                  {formatDecimal(score.percentage)}%
                </span>
                {score.is_passed ? (
                  <FiCheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <FiXCircle className="w-6 h-6 text-red-500" />
                )}
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                {score.exam?.title || 'Exam'}
              </h4>
              <ExamTypeBadge exam={score.exam} />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {score.course?.title || 'Course'}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Score: {score.score || 0} points
              </p>
            </motion.div>
          ))}
        </div>
      )}
    </div>

    {/* Course Results */}
    {courseResults.length > 0 && (
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiTrendingUp className="w-4 h-4 text-blue-500" />
          Course Final Results
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Course</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Final Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Percentage</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {courseResults.map((result, index) => (
                <tr key={index} className="bg-white dark:bg-gray-800">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    {result.course?.title || 'Course'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                    {formatDecimal(result.final_score)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                    {formatDecimal(result.percentage)}%
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      result.status === 'P'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : result.status === 'R'
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {result.status === 'P' ? 'Passed' : result.status === 'R' ? 'Retake' : result.status === 'H' ? 'Hold' : 'Dropped'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
);

// Empty State Component
const EmptyState = ({ icon: Icon, message }) => (
  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
    <Icon className="w-12 h-12 text-gray-400 mb-4" />
    <p className="text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

export default StudentHistory;
